import React from 'react';
import { useIndustryQuery, useSupplierTypesListQuery } from 'api';
import {
  Col,
  DatePicker,
  Divider,
  Form,
  FormInstance,
  LanguageSelect,
  Row,
  Select,
  SupplierSelect,
  Tag,
  Typography,
} from 'components';
import { CountrySelect, FilterDrawer } from 'components/common';
import { CalendarIcon } from 'components/common/icons';
import styles from 'pages/admin/clients/styles.module.scss';
import { useFilters } from './useFilters';

const { RangePicker } = DatePicker;

interface IFilters {
  form: FormInstance;
  drawerOpen: boolean;
  closeDrawer: () => void;
  applyFilters: (value: Record<string, any>) => void;
  resetFilters: () => void;
}

export const Filters = React.memo((props: IFilters) => {
  const { form, drawerOpen, closeDrawer, applyFilters, resetFilters } = props;
  const { data: industryData = [] } = useIndustryQuery();
  const { data: typeData = [] } = useSupplierTypesListQuery();
  const { handleTagSelect, handleTagRemove, availableTags, clearFilters } = useFilters({ form, resetFilters });

  return (
    <FilterDrawer
      title="Filter"
      placement="left"
      width={471}
      styles={{
        body: {
          marginBottom: 80,
        },
      }} // Add space for drawer actions
      onClose={closeDrawer}
      open={drawerOpen}
      onApply={() => form.submit()}
      onClear={clearFilters}
      onExpand={closeDrawer}
    >
      <Form layout="vertical" form={form} onFinish={applyFilters}>
        <Row gutter={[0, 16]}>
          <Col span={24}>
            <Form.Item name="supplierId" label={'Supplier'}>
              <SupplierSelect />
            </Form.Item>
          </Col>
          <Col span={24}>
            <Form.Item name="countryId" label={'Country'}>
              <CountrySelect />
            </Form.Item>
          </Col>
          <Col span={24}>
            <Form.Item name="languageId" label={'Language'}>
              <LanguageSelect />
            </Form.Item>
          </Col>
          <Col span={24}>
            <Form.Item name="industryId" label={'Industry'}>
              <Select options={industryData} />
            </Form.Item>
          </Col>
          <Col span={24}>
            <Form.Item name="type" label={'Type'}>
              <Select options={typeData} />
            </Form.Item>
          </Col>
          <Col span={24}>
            <Form.Item name="createdDate" label={'Created Date'}>
              <RangePicker
                suffixIcon={<CalendarIcon />}
                className={styles.range_picker}
                popupClassName={'fmg-range-picker'}
              />
            </Form.Item>
          </Col>
          <Col span={24}>
            <Form.Item name="lastUpdatedDate" label={'Last Updated Date'}>
              <RangePicker
                suffixIcon={<CalendarIcon />}
                className={styles.range_picker}
                popupClassName={'fmg-range-picker'}
              />
            </Form.Item>
          </Col>
          <Col span={24}>
            <Form.Item label={'Tags'}>
              <Form.List name="tags">
                {(fields, { add, remove }) => (
                  <Row gutter={[0, 12]}>
                    <Col>
                      <Row gutter={[6, 6]}>
                        {fields.map(({ key, name }) => (
                          <Col key={key} style={{ height: 26 }}>
                            <Tag
                              className={'custom-tag'}
                              title={form.getFieldValue(['tags', name])}
                              bordered={false}
                              closable
                              onClose={() => {
                                const removedTag = form.getFieldValue(['tags', name]);
                                handleTagRemove(removedTag);
                                remove(name);
                              }}
                            >
                              {form.getFieldValue(['tags', name])}
                            </Tag>
                          </Col>
                        ))}
                      </Row>
                    </Col>
                    <Divider />
                    <Col>
                      <Row gutter={[6, 6]}>
                        {availableTags?.length ? (
                          availableTags.map((tag: string) => (
                            <Col key={tag} style={{ height: 26 }}>
                              <Tag
                                bordered={false}
                                className={'custom-tag'}
                                title={tag}
                                onClick={() => {
                                  add(tag);
                                  handleTagSelect(tag);
                                }}
                                style={{
                                  cursor: 'pointer',
                                }}
                              >
                                {tag}
                              </Tag>
                            </Col>
                          ))
                        ) : !fields?.length ? (
                          <Typography> Not Set </Typography>
                        ) : null}
                      </Row>
                    </Col>
                  </Row>
                )}
              </Form.List>
            </Form.Item>
          </Col>
        </Row>
      </Form>
    </FilterDrawer>
  );
});
